import { useEffect, useState } from 'react';

// Floating toolbar that appears above the user's text selection while
// editing a rich-text Editable. We poll selectionchange + scroll/resize so
// the toolbar follows the selection accurately.
//
// `containerRef` constrains the toolbar to selections inside that element —
// otherwise unrelated selections elsewhere on the page would trigger it.
export default function RichTextToolbar({ containerRef }) {
  const [pos, setPos] = useState(null); // { left, top } in viewport coords

  useEffect(() => {
    function recompute() {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
        setPos(null);
        return;
      }
      const range = sel.getRangeAt(0);
      const container = containerRef.current;
      if (!container || !container.contains(range.commonAncestorContainer)) {
        setPos(null);
        return;
      }
      const rect = range.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        setPos(null);
        return;
      }
      setPos({ left: rect.left + rect.width / 2, top: rect.top });
    }

    document.addEventListener('selectionchange', recompute);
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      document.removeEventListener('selectionchange', recompute);
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [containerRef]);

  // execCommand is deprecated but still works in every modern browser and
  // remains the lowest-friction way to do simple inline formatting on
  // contentEditable. Replacing it would mean adopting TipTap/Lexical.
  function exec(cmd, value) {
    document.execCommand(cmd, false, value);
  }

  function makeLink() {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return;
    const url = window.prompt('Link URL', 'https://');
    if (!url) return;
    exec('createLink', url);
  }

  function unlink() {
    exec('unlink');
  }

  if (!pos) return null;

  // Mousedown is what kills the selection in browsers — preventing default
  // here keeps the selection intact while the user clicks a button.
  const stop = (e) => e.preventDefault();

  return (
    <div
      style={{
        position: 'fixed',
        left: pos.left,
        top: pos.top - 8,
        transform: 'translate(-50%, -100%)',
        zIndex: 60,
      }}
      onMouseDown={stop}
      className="flex items-center gap-0.5 rounded-lg border border-ink-800 bg-ink-900 px-1 py-1 text-white shadow-xl"
    >
      <Btn onClick={() => exec('bold')} title="Bold (Ctrl+B)"><b>B</b></Btn>
      <Btn onClick={() => exec('italic')} title="Italic (Ctrl+I)"><i>I</i></Btn>
      <Btn onClick={makeLink} title="Add link">⌘</Btn>
      <Btn onClick={unlink} title="Remove link">⊘</Btn>
    </div>
  );
}

function Btn({ children, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className="grid h-7 w-7 place-items-center rounded text-xs hover:bg-ink-800"
    >
      {children}
    </button>
  );
}
